import { useState, useEffect, useMemo } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Clock, AlertCircle } from 'lucide-react';

function EventItem({ event }) {
  const startTime = event.start?.dateTime
    ? new Date(event.start.dateTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : 'All day';

  return (
    <div className="flex items-center gap-3 py-2">
      <div
        className="w-1 h-8 rounded-full"
        style={{ backgroundColor: event.colorId ? `var(--gcal-${event.colorId})` : 'var(--accent)' }}
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-text-primary truncate">{event.summary || 'Untitled'}</p>
        <p className="text-xs text-text-tertiary">{startTime}</p>
      </div>
    </div>
  );
}

function DayColumn({ date, events, isToday, isPast }) {
  const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
  const dayNum = date.getDate();

  return (
    <div className={`
      flex-1 min-w-0 p-3 rounded-lg
      ${isToday ? 'bg-accent/10 border border-accent/30' : isPast ? 'bg-surface-hover/50' : 'bg-surface-card'}
    `}>
      <div className="text-center mb-2">
        <p className={`text-xs ${isPast ? 'text-text-tertiary' : 'text-text-secondary'}`}>{dayName}</p>
        <p className={`text-lg font-semibold ${isToday ? 'text-accent' : isPast ? 'text-text-tertiary' : 'text-text-primary'}`}>
          {dayNum}
        </p>
      </div>
      <div className="space-y-1">
        {events.length === 0 ? (
          <p className="text-xs text-text-tertiary text-center py-2">No events</p>
        ) : (
          events.slice(0, 4).map((event, idx) => (
            <EventItem key={event.id || idx} event={event} />
          ))
        )}
        {events.length > 4 && (
          <p className="text-xs text-text-tertiary text-center">+{events.length - 4} more</p>
        )}
      </div>
    </div>
  );
}

export default function CalendarReview({ weekBounds, gcalSignedIn, getAllEventsForDate }) {
  const [pastWeekEvents, setPastWeekEvents] = useState({});
  const [nextWeekEvents, setNextWeekEvents] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('past'); // 'past' or 'upcoming'

  // Generate date ranges
  const pastWeekDates = useMemo(() => {
    const dates = [];
    const start = new Date(weekBounds.start);
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, [weekBounds]);

  const nextWeekDates = useMemo(() => {
    const dates = [];
    const start = new Date(weekBounds.end);
    start.setDate(start.getDate() + 1);
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, [weekBounds]);

  // Fetch events
  useEffect(() => {
    const fetchEvents = async () => {
      if (!gcalSignedIn || !getAllEventsForDate) {
        setLoading(false);
        return;
      }

      setLoading(true);
      const pastEvents = {};
      const nextEvents = {};

      try {
        // Fetch past week
        for (const date of pastWeekDates) {
          const dateStr = date.toISOString().split('T')[0];
          const events = await getAllEventsForDate(dateStr);
          pastEvents[dateStr] = events;
        }

        // Fetch next week
        for (const date of nextWeekDates) {
          const dateStr = date.toISOString().split('T')[0];
          const events = await getAllEventsForDate(dateStr);
          nextEvents[dateStr] = events;
        }

        setPastWeekEvents(pastEvents);
        setNextWeekEvents(nextEvents);
      } catch (error) {
        console.error('Error fetching calendar events:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [gcalSignedIn, getAllEventsForDate, pastWeekDates, nextWeekDates]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (!gcalSignedIn) {
    return (
      <div className="flex-1 overflow-auto p-4 md:p-6">
        <div className="max-w-2xl mx-auto text-center py-12">
          <div className="w-16 h-16 rounded-2xl bg-surface-hover flex items-center justify-center mx-auto mb-4">
            <Calendar size={32} className="text-text-tertiary" />
          </div>
          <h2 className="text-xl font-semibold text-text-primary mb-2">Calendar Review</h2>
          <p className="text-text-secondary mb-4">
            Connect Google Calendar in Settings to review your schedule.
          </p>
          <div className="inline-flex items-center gap-2 text-sm text-text-tertiary bg-surface-hover px-3 py-2 rounded-lg">
            <AlertCircle size={14} />
            Calendar not connected
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-weekly/10 flex items-center justify-center mx-auto mb-4">
            <Calendar size={32} className="text-weekly" />
          </div>
          <h2 className="text-xl font-semibold text-text-primary mb-2">Calendar Review</h2>
          <p className="text-text-secondary">
            Review past meetings for follow-ups and preview upcoming commitments.
          </p>
        </div>

        {/* View toggle */}
        <div className="flex justify-center gap-2 mb-6">
          <button
            onClick={() => setViewMode('past')}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
              border-none cursor-pointer transition-all
              ${viewMode === 'past'
                ? 'bg-weekly text-white'
                : 'bg-surface-hover text-text-secondary hover:text-text-primary'
              }
            `}
          >
            <ChevronLeft size={16} />
            This Week
          </button>
          <button
            onClick={() => setViewMode('upcoming')}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
              border-none cursor-pointer transition-all
              ${viewMode === 'upcoming'
                ? 'bg-weekly text-white'
                : 'bg-surface-hover text-text-secondary hover:text-text-primary'
              }
            `}
          >
            Next Week
            <ChevronRight size={16} />
          </button>
        </div>

        {/* Calendar grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 rounded-lg bg-weekly/20 animate-pulse" />
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-2">
            {(viewMode === 'past' ? pastWeekDates : nextWeekDates).map(date => {
              const dateStr = date.toISOString().split('T')[0];
              const events = viewMode === 'past'
                ? pastWeekEvents[dateStr] || []
                : nextWeekEvents[dateStr] || [];
              const dateOnly = new Date(date);
              dateOnly.setHours(0, 0, 0, 0);

              return (
                <DayColumn
                  key={dateStr}
                  date={date}
                  events={events}
                  isToday={dateOnly.getTime() === today.getTime()}
                  isPast={viewMode === 'past'}
                />
              );
            })}
          </div>
        )}

        {/* Tips */}
        <div className="mt-6 p-4 bg-surface-hover/50 rounded-xl">
          <p className="text-sm text-text-tertiary">
            <strong className="text-text-secondary">
              {viewMode === 'past' ? 'Past Week:' : 'Next Week:'}
            </strong>{' '}
            {viewMode === 'past'
              ? 'Any meetings that need follow-up actions? Add them to your inbox.'
              : 'Any prep needed for upcoming meetings? Block time now.'
            }
          </p>
        </div>
      </div>
    </div>
  );
}
